import { ProjectAssigneeRateHistory } from '../ProjectAssigneeRateHistory';
import { ProjectAssigneeCurrencyHistory } from '../ProjectAssigneeCurrencyHistory';
import { ProjectAssigneeAssignmentHistory } from '../ProjectAssigneeAssignmentHistory';
import { MemberCard } from '../MemberCard';
import { ProjectAssigneesWithUserInfoDto } from '../../../commonTypes/project.types';
import { PublicUserDto } from '../../../commonTypes/user.types';
import { useTranslation } from '../../../hooks/useTranslation';

export type OverviewAssignmentView = 'members' | 'rateHistory' | 'currencyHistory' | 'assignmentHistory';

const OVERVIEW_ASSIGNMENT_VIEWS = {
    members: 'members',
    rateHistory: 'rateHistory',
    currencyHistory: 'currencyHistory',
    assignmentHistory: 'assignmentHistory',
} as const;

type OverviewAssignmentSectionProps = {
    showRates: boolean;
    view: OverviewAssignmentView;
    onViewChange: (view: OverviewAssignmentView) => void;
    activeMembers: ProjectAssigneesWithUserInfoDto[];
    assignees: ProjectAssigneesWithUserInfoDto[];
    organizationMembers: PublicUserDto[];
    canEdit: boolean;
    currency: string;
    projectId: string;
    projectStartDate?: string;
    rateHistoryRefreshKey: number;
    onEditMember: (member: ProjectAssigneesWithUserInfoDto) => void;
    onOpenAddMemberDrawer: () => void;
};

export const OverviewAssignmentSection = ({
    showRates,
    view,
    onViewChange,
    activeMembers,
    assignees,
    organizationMembers,
    canEdit,
    currency,
    projectId,
    projectStartDate,
    rateHistoryRefreshKey,
    onEditMember,
    onOpenAddMemberDrawer,
}: OverviewAssignmentSectionProps) => {
    const { t } = useTranslation();

    const getSectionTitle = () => {
        if (!showRates || view === OVERVIEW_ASSIGNMENT_VIEWS.members) {
            return t('projects.members');
        }

        if (view === OVERVIEW_ASSIGNMENT_VIEWS.currencyHistory) {
            return t('projects.assigneeCurrencyHistoryHeading');
        }

        if (view === OVERVIEW_ASSIGNMENT_VIEWS.assignmentHistory) {
            return t('projects.assigneeAssignmentHistoryHeading');
        }

        return t('projects.assigneeRateHistoryHeading');
    };

    return (
        <div className="project-overview-tab__section">
            <div className="project-overview-tab__section-header project-overview-tab__section-header--with-subtabs">
                <h3 className="heading-4">{getSectionTitle()}</h3>
                {showRates && (
                    <div className="project-overview-tab__subtabs" role="tablist" aria-label={t('projects.assigneeAssignmentSubtabListAriaLabel')}>
                        <button
                            type="button"
                            role="tab"
                            id="project-overview-tab-members"
                            aria-selected={view === OVERVIEW_ASSIGNMENT_VIEWS.members}
                            className={`paragraph-4 project-overview-tab__subtab ${
                                view === OVERVIEW_ASSIGNMENT_VIEWS.members ? 'project-overview-tab__subtab--active' : ''
                            }`}
                            onClick={() => onViewChange(OVERVIEW_ASSIGNMENT_VIEWS.members)}
                        >
                            {t('projects.members')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            id="project-overview-tab-rate-history"
                            aria-selected={view === OVERVIEW_ASSIGNMENT_VIEWS.rateHistory}
                            className={`paragraph-4 project-overview-tab__subtab ${
                                view === OVERVIEW_ASSIGNMENT_VIEWS.rateHistory ? 'project-overview-tab__subtab--active' : ''
                            }`}
                            onClick={() => onViewChange(OVERVIEW_ASSIGNMENT_VIEWS.rateHistory)}
                        >
                            {t('projects.assigneeRateHistoryTab')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            id="project-overview-tab-currency-history"
                            aria-selected={view === OVERVIEW_ASSIGNMENT_VIEWS.currencyHistory}
                            className={`paragraph-4 project-overview-tab__subtab ${
                                view === OVERVIEW_ASSIGNMENT_VIEWS.currencyHistory ? 'project-overview-tab__subtab--active' : ''
                            }`}
                            onClick={() => onViewChange(OVERVIEW_ASSIGNMENT_VIEWS.currencyHistory)}
                        >
                            {t('projects.assigneeCurrencyHistoryTab')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            id="project-overview-tab-assignment-history"
                            aria-selected={view === OVERVIEW_ASSIGNMENT_VIEWS.assignmentHistory}
                            className={`paragraph-4 project-overview-tab__subtab ${
                                view === OVERVIEW_ASSIGNMENT_VIEWS.assignmentHistory ? 'project-overview-tab__subtab--active' : ''
                            }`}
                            onClick={() => onViewChange(OVERVIEW_ASSIGNMENT_VIEWS.assignmentHistory)}
                        >
                            {t('projects.assigneeAssignmentHistoryTab')}
                        </button>
                    </div>
                )}
            </div>

            {(!showRates || view === OVERVIEW_ASSIGNMENT_VIEWS.members) && (
                <div className="project-overview-tab__members">
                    {activeMembers.length ? (
                        <>
                            {activeMembers.map(m => (
                                <MemberCard
                                    key={m.userSub}
                                    member={m}
                                    canEdit={canEdit}
                                    showRates={showRates}
                                    onEdit={onEditMember}
                                    currency={currency}
                                />
                            ))}
                            {canEdit && (
                                <div className="member-card__add-member" onClick={onOpenAddMemberDrawer}>
                                    <div className="member-card__add-member-circle">
                                        <img src="/svg/plus.svg" alt="Add member" title="Add member" />
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="member-card__add-member" onClick={onOpenAddMemberDrawer}>
                            <div className="member-card__add-member-circle">
                                <img src="/svg/plus.svg" alt="Add member" title="Add member" />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showRates && view === OVERVIEW_ASSIGNMENT_VIEWS.rateHistory && (
                <ProjectAssigneeRateHistory
                    projectId={projectId}
                    projectStartDate={projectStartDate}
                    currency={currency}
                    assignees={assignees}
                    organizationMembers={organizationMembers}
                    enabled
                    refreshKey={rateHistoryRefreshKey}
                />
            )}

            {showRates && view === OVERVIEW_ASSIGNMENT_VIEWS.currencyHistory && (
                <ProjectAssigneeCurrencyHistory
                    projectId={projectId}
                    projectStartDate={projectStartDate}
                    assignees={assignees}
                    organizationMembers={organizationMembers}
                    enabled
                    refreshKey={rateHistoryRefreshKey}
                />
            )}

            {showRates && view === OVERVIEW_ASSIGNMENT_VIEWS.assignmentHistory && (
                <ProjectAssigneeAssignmentHistory
                    projectId={projectId}
                    projectStartDate={projectStartDate}
                    assignees={assignees}
                    organizationMembers={organizationMembers}
                    enabled
                    refreshKey={rateHistoryRefreshKey}
                />
            )}
        </div>
    );
};
